import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { stringSearch } from '../../utils/string.utils';
import { KEY_CODE } from '../../constants/keycodes';
import { HcTableDataSource, SearchBarComponent } from '@healthcatalyst/cashmere';
import { Observable, filter } from 'rxjs';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import BaseComponent from '../base.component';
import { TenantSource } from '../../models/tenant-source';
import { TenantSourceService } from 'src/app/core/services/application/tenant-source.service';

import { SourceProcessingService } from '../../source-processing/source-processing.service';
import { SourceListItemVM } from '../../models/source-list-item';
import { TenantSourceWithConnectionService } from 'src/app/core/services/application/tenant-source-with-connection.service';

@Component({
    selector: 'cdp-source-switcher',
    templateUrl: './source-switcher.component.html',
    styleUrls: ['./source-switcher.component.scss'],
    standalone: false
})
export class SourceSwitcherComponent extends BaseComponent implements OnInit {
    @Input() selectedSourceId: number | null = null;
    @Input({ required: true }) tenantId: string;
    @Input() isInDropdown = true;
    @Output() sourceSelected = new EventEmitter<TenantSource | undefined>();
    @ViewChild('sourceSearch') searchBar!: SearchBarComponent;
    @ViewChildren('sourceRow') sourceRows!: QueryList<ElementRef>;
    sourcesDataSource = new HcTableDataSource<SourceListItemVM>();
    loading = false;
    loadingFailed = false;
    private loggedInUser$!: Observable<AuthState>;

    constructor(
        private sourceService: TenantSourceService,
        private sourceProcessingService: SourceProcessingService,
        private _oktaAuthStateService: OktaAuthStateService,
        private tenantSourceWithConnectionService: TenantSourceWithConnectionService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.loggedInUser$ = this._oktaAuthStateService.authState$.pipe(
            filter((authState: AuthState) => !!authState && !!authState.isAuthenticated)
        );

        this.loggedInUser$.pipe(this.unsubscribeOnDestroy()).subscribe(() => {
            this.loadSources();
            this.sourceService.tenantSourceCacheCleared$.pipe(this.unsubscribeOnDestroy()).subscribe(() => {
                this.loadSources();
            });
        });

        setTimeout(() => {
            this.searchBar?.focus();
        });
    }

    async loadSources(): Promise<void> {
        this.loading = true;
        try {
            const sources = await this.tenantSourceWithConnectionService.getCachedTenantSourcesWithConnection(
                this.tenantId
            );
            const sourceVMs = this.sourceProcessingService.processSources(sources);
            this.sourcesDataSource = new HcTableDataSource(sourceVMs);
            this.sourcesDataSource.filterPredicate = this.filterPredicate;
            this.loadingFailed = false;
        } catch (e) {
            console.error(`Failed to load sources: ${e}`);
            this.sourcesDataSource = new HcTableDataSource(new Array<SourceListItemVM>());
            this.loadingFailed = true;
        } finally {
            this.loading = false;
        }
    }

    filterPredicate(data: SourceListItemVM, searchTerm: string) {
        const subject = `${data.description} ${data.connectionType}`;
        return stringSearch(subject, searchTerm);
    }

    clearSearch(): void {
        this.searchBar.clear();
        this.sourcesDataSource.filter = '';
    }

    search(term: string): void {
        this.sourcesDataSource.filter = term;
    }

    async selectSource(source?: TenantSource): Promise<void> {
        this.selectedSourceId = source?.tenantSourceId || 0;
        const sourceToEmit = this.sourcesDataSource.data.find(s => s.tenantSourceId === source?.tenantSourceId);
        this.sourceSelected.emit(sourceToEmit);
    }

    onSearchKeydown(event: KeyboardEvent): void {
        if (event.which === KEY_CODE.DOWN_ARROW) {
            this.sourceRows.first?.nativeElement.focus();
        }
    }

    onRowKeydown(event: KeyboardEvent, source?: TenantSource): void {
        if (event.which !== KEY_CODE.TAB) {
            event.preventDefault();
        }
        switch (event.which) {
            case KEY_CODE.ENTER:
                this.selectSource(source);
                break;
            case KEY_CODE.DOWN_ARROW:
                this.shiftFocusedRow(event);
                break;
            case KEY_CODE.UP_ARROW:
                this.shiftFocusedRow(event, false);
                break;
            default:
                return;
        }
    }

    private shiftFocusedRow(event: KeyboardEvent, shiftFocusDown = true) {
        const rows = this.sourceRows.toArray();
        const currentIndex = rows.findIndex(el => el.nativeElement === event.target);
        const maxIndex = rows.length - 1;
        let focusIndex = currentIndex + (shiftFocusDown ? 1 : -1);
        // pressing down key on last row jumps you back to top, pressing up key on first row jumps you to bottom
        focusIndex = focusIndex < 0 ? maxIndex : focusIndex > maxIndex ? 0 : focusIndex;
        rows[focusIndex]?.nativeElement.focus();
    }
}
